import { Link, useNavigate, useParams } from "react-router-dom";
import { apiClient } from "@api/index";
import { useThemeStore } from "@state/theme";
import { useConfigStore } from "@state/config";
import { useAuthStore } from "@state/auth";
import moment from "moment";
import EventStatusChip from "@components/EventStatusChip";
import BackArrowIcon from "@mui/icons-material/ArrowBack";

const PlayBack = () => {
  const params = useParams();
  const navigate = useNavigate();

  const { clientID, eventID, appID } = params;
  const user = useAuthStore((state) => state.user);

  const { setTheme } = useThemeStore();
  const { setConfig } = useConfigStore();

  const { data, isLoading: isAppLoading } = apiClient.ott.getAppByID.useQuery(
    {
      appID: appID as string,
      clientID: user.clientID,
    },
    {
      refetchOnWindowFocus: false,
      onSuccess(res) {
        const { manifest, appName, targets } = res;
  
        const { data } = manifest;
        const { theme, config } = data;
  
        setTheme(theme);
        setConfig(config);
      },
    }
  );
  
  const { data: event, isLoading: isEventLoading } = apiClient.event?.getEventByEventID.useQuery({
    eventID: eventID!,
    clientID: clientID!,
    includePrivate: true,
  });

  const { textColor, primaryColor: primaryButtonColor } = useThemeStore();
  const { thumbMissingUrl } = useConfigStore();

  if (!event || isAppLoading || isEventLoading) {
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <div className="border-gray-300 animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-l-0 border-r-4"></div>
      </div>
    );
  }

  const description = event?.description?.replace(/<\/?[^>]+(>|$)/g, "");

  const handleBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // Prevent default Link behavior
    if (window.history.length > 2) {
      navigate(-1); // Go back in history if available
    } else {
      navigate("/"); // Fallback to home page or any specific route
    }
  };

  return (
    <div className="relative w-full min-h-screen h-screen flex items-start">

      <div className="absolute inset-0 flex flex-row items-center justify-center">
        <div className="relative flex w-full h-full bg-black">
          <div className="w-1/4 h-full" />

          <div className="absolute top-0 left-1/4 w-1/2 h-full bg-gradient-to-r from-black to-transparent opacity-100" />

          <img
            className="w-3/4 h-full object-cover"
            src={event?.videoThumbnail ? event?.videoThumbnail : thumbMissingUrl}
            alt="Event thumbnail"
          />
        </div>
      </div>

      <div className="z-[1] pl-12 py-20 flex flex-col justify-start">
        <div
          className="text-2xl max-w-[32rem] font-semibold tracking-wide leading-tight"
          style={{ color: textColor }}
        >
          {event?.title}
        </div>

        <div className="flex flex-row gap-4">
          {event?.startDateTime && (
            <div
              className="text-sm"
              style={{ color: textColor }}
            >
              <>
                {moment(event?.startDateTime).isSameOrAfter(moment(), "day") ? (
                  <>Upcoming {moment(event?.startDateTime).format("dddd Do")}</>
                ) : (
                  <>{moment(event?.startDateTime).format("dddd, MMM Do YYYY [at] h:mm a")}</>
                )}
              </>
            </div>
          )}

          <EventStatusChip status={event?.eventStatus} />
        </div>

        <div
          className="text-base font-semibold max-w-md line-clamp-5"
          style={{ color: textColor }}
        >
          {description}
        </div>

        <Link
          to={`https://player.invintus.com/?clientID=${event?.clientID}&eventID=${event?.eventID}`}
          target="_blank"
          rel="noopener noreferrer"
          className="px-14 w-min mt-2 whitespace-nowrap text-center py-2 block font-bold rounded-md shadow-lg"
          style={{
            color: textColor,
            backgroundColor: primaryButtonColor,
          }}
        >
          Watch Now
        </Link>
        <div
          className="px-14 w-min mt-2 whitespace-nowrap text-center py-2 block font-bold rounded-md shadow-lg"
          style={{
            color: textColor,
            backgroundColor: primaryButtonColor,
          }}
        >
          Go Back
        </div>
      </div>
    </div>
  );
};

export default PlayBack;
